import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { triggerAlert } from "../../utils";
import { AdminOrganizations } from "../../utils/APIClient";

/**
 * Give a customer a phone number they cannot buy themselves — one ported in
 * from another carrier, or one sourced manually and handed over at an agreed
 * rate.
 *
 * The price is per number and lives on the subscription, so two customers can
 * hold equivalent numbers at different rates without a plan per customer.
 *
 * A price of 0 is a real choice, not a placeholder: it creates no Authorize.net
 * subscription at all, so the number is genuinely free and the customer needs no
 * card on file. Anything above 0 charges the first month immediately and opens a
 * monthly recurring charge, which does require a saved card — the backend
 * refuses with a clear message if there isn't one.
 */

const LINE_TYPES = [
    { value: "local", label: "Local" },
    { value: "toll_free", label: "Toll-free" },
    { value: "mobile", label: "Mobile" },
];

const SOURCES = [
    { value: "ported", label: "Ported in from another carrier" },
    { value: "assigned", label: "Assigned manually" },
];

const empty = () => ({
    phone_number_e164: "",
    line_type: "local",
    source: "ported",
    monthly_price: "0",
    note: "",
    skip_vendor_check: false,
});

export default function AssignPhoneNumberDialog({ orgId, orgName, prefill, onClose, onAssigned }) {
    // `prefill` arrives when reassigning a number the customer already holds —
    // it kept its row through the unassign, so the admin should not have to
    // retype a number that is on screen in front of them.
    const [form, setForm] = useState(() => ({ ...empty(), ...(prefill || {}) }));
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        const num = form.phone_number_e164.trim();
        if (!num) {
            e.phone_number_e164 = "Enter the number.";
        } else if (!/^\+\d{7,15}$/.test(num)) {
            e.phone_number_e164 =
                "Use E.164 format — a plus sign then digits only, e.g. +14155550123.";
        }
        const price = Number(form.monthly_price);
        if (form.monthly_price === "" || Number.isNaN(price)) {
            e.monthly_price = "Enter a monthly price, or 0 to make it free.";
        } else if (price < 0) {
            e.monthly_price = "The price cannot be negative.";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const isFree = Number(form.monthly_price) === 0;

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSaving(true);
        try {
            await AdminOrganizations.assignPhoneNumber(orgId, {
                phone_number_e164: form.phone_number_e164.trim(),
                line_type: form.line_type,
                source: form.source,
                monthly_price: form.monthly_price,
                note: form.note.trim(),
                skip_vendor_check: form.skip_vendor_check ? "true" : "",
            });
            triggerAlert(
                "success",
                "Assigned",
                isFree
                    ? `${form.phone_number_e164} added at no charge.`
                    : `${form.phone_number_e164} added at $${Number(form.monthly_price).toFixed(2)}/mo.`,
            );
            onAssigned && onAssigned();
        } catch (e) {
            const data = e?.response?.data;
            if (data && typeof data === "object" && !data.error) {
                setErrors(
                    Object.fromEntries(
                        Object.entries(data).map(([k, v]) => [
                            k,
                            Array.isArray(v) ? v.join(" ") : String(v),
                        ]),
                    ),
                );
            }
            triggerAlert(
                "error",
                "Oops...",
                data?.error || data?.detail || "Could not assign the number.",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show onHide={saving ? undefined : onClose} size="lg" backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>Assign a phone number{orgName ? ` — ${orgName}` : ""}</Modal.Title>
            </Modal.Header>

            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    <div className="alert alert-info py-2">
                        {prefill
                            ? "This number is already on the customer's account with its billing "
                              + "stopped. Assigning it again starts a new subscription at the price "
                              + "you set below — the old one stays in their billing history."
                            : "The number must already exist in the carrier account. This does not "
                              + "buy a number — it records one the customer already has and sets up "
                              + "the billing for it."}
                    </div>

                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">
                                Phone number <span className="text-danger">*</span>
                            </label>
                            <input
                                className={`form-control ${errors.phone_number_e164 ? "is-invalid" : ""}`}
                                placeholder="+14155550123"
                                style={{ fontFamily: "monospace" }}
                                value={form.phone_number_e164}
                                onChange={(e) => setField("phone_number_e164", e.target.value)}
                            />
                            {errors.phone_number_e164 && (
                                <div className="invalid-feedback">{errors.phone_number_e164}</div>
                            )}
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Line type</label>
                            <select
                                className="form-select"
                                value={form.line_type}
                                onChange={(e) => setField("line_type", e.target.value)}
                            >
                                {LINE_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">
                                Monthly price <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                                <span className="input-group-text">$</span>
                                <input
                                    type="number" min="0" step="0.01"
                                    className={`form-control ${errors.monthly_price ? "is-invalid" : ""}`}
                                    value={form.monthly_price}
                                    onChange={(e) => setField("monthly_price", e.target.value)}
                                />
                                {errors.monthly_price && (
                                    <div className="invalid-feedback">{errors.monthly_price}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label">How they got it</label>
                            <select
                                className="form-select"
                                value={form.source}
                                onChange={(e) => setField("source", e.target.value)}
                            >
                                {SOURCES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                            <div className="form-text">
                                Either way the number is never released back to the carrier
                                automatically — it is theirs to keep.
                            </div>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label">Internal note</label>
                            <input
                                className="form-control"
                                placeholder="Agreed rate, ticket reference…"
                                value={form.note}
                                onChange={(e) => setField("note", e.target.value)}
                            />
                        </div>

                        <div className="col-12">
                            <div
                                className={`p-3 rounded border ${isFree ? "bg-light" : "border-warning"}`}
                            >
                                {isFree ? (
                                    <span className="small">
                                        <strong>Free.</strong> No recurring charge is created and the
                                        customer does not need a card on file.
                                    </span>
                                ) : (
                                    <span className="small">
                                        <strong>
                                            ${Number(form.monthly_price || 0).toFixed(2)} per month.
                                        </strong>{" "}
                                        The first month is charged now and a monthly recurring
                                        charge is set up. This needs a saved card — if there
                                        isn't one, assign at $0 instead.
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="apn_skip_check"
                                    checked={form.skip_vendor_check}
                                    onChange={(e) => setField("skip_vendor_check", e.target.checked)}
                                />
                                <label className="form-check-label small" htmlFor="apn_skip_check">
                                    Skip the carrier check
                                </label>
                                <div className="form-text">
                                    We normally confirm the number is in the carrier account first,
                                    because otherwise it can never route calls. Only skip this for a
                                    port that has just completed and has not propagated yet.
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-light"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? "Assigning…" : "Assign number"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
